import { useMemo, useState } from "react";
import { Tabs } from "../../../shared/Tabs";
import { SettingsHeader } from "../index";
import { Modal } from "../../../shared";
import {
  useDeleteTemplateMutation,
  useGetTemplatesQuery,
} from "../../../redux/services/templates/api";
import { toast } from "react-toastify";
import { useConfig } from "../../../hooks";
import { capitalizeFirstLetter } from "../../../utils";
import { getProductTerminology } from "../../../constants/product-terminology";
import TemplateListPanel from "./TemplateListPanel";
import JobTemplateModal from "./JobTemplateModal";
import LegTemplateModal from "./LegTemplateModal";
import { TEMPLATE_TYPES } from "./template.constants";
import { getOperationalTemplateProfile } from "./template.profile";
import { templateMatchesAppType } from "./template.utils";

const Templates = () => {
  const { appType } = useConfig();
  const terms = getProductTerminology(appType);
  const profile = getOperationalTemplateProfile(appType);

  const [modalState, setModalState] = useState({
    visible: false,
    type: TEMPLATE_TYPES.JOB,
    mode: "create",
    template: null,
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmContent, setConfirmContent] = useState(null);

  const { data, isLoading, isError } = useGetTemplatesQuery({ limit: 100 });
  const [deleteTemplate] = useDeleteTemplateMutation();

  const groupedTemplates = useMemo(() => {
    return (data?.templates || [])
      .filter((template) => templateMatchesAppType(template, appType))
      .reduce((acc, template) => {
        acc[template.type] = [...(acc[template.type] || []), template];
        return acc;
      }, {});
  }, [data?.templates, appType]);

  function openTemplateModal(type, mode = "create", template = null) {
    setModalState({
      visible: true,
      type,
      mode,
      template,
    });
  }

  function handleConfirmModal(type, template) {
    setConfirmContent({
      title: `${capitalizeFirstLetter(type)} Template`,
      content: `Are you sure you want to ${type} this template?`,
      template,
      type,
    });
    setShowConfirmModal(true);
  }

  async function handleDeleteTemplate(template) {
    try {
      const response = await deleteTemplate(template._id).unwrap();
      if (response.status !== "success") {
        toast.error("Failed to delete template");
        return;
      }
      toast.success("Template deleted successfully");
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error(
        error.message || "An error occurred while deleting the template",
      );
    }
  }

  function handleConfirm(type) {
    if (type === "delete") {
      handleDeleteTemplate(confirmContent.template);
    }
    setShowConfirmModal(false);
    handleCleanUp();
  }

  function handleCleanUp() {
    setConfirmContent(null);
    setModalState({
      visible: false,
      type: TEMPLATE_TYPES.JOB,
      mode: "create",
      template: null,
    });
  }

  const renderOperationalPanel = ({
    type,
    title,
    subtitle,
    emptyDescription,
    addLabel,
  }) => (
    <TemplateListPanel
      title={title}
      subtitle={subtitle}
      templates={groupedTemplates[type] || []}
      isLoading={isLoading}
      isError={isError}
      addLabel={addLabel}
      emptyDescription={emptyDescription}
      onAdd={() => openTemplateModal(type)}
      onEdit={(template) => openTemplateModal(type, "edit", template)}
      onDuplicate={(template) => openTemplateModal(type, "duplicate", template)}
      onDelete={(template) => handleConfirmModal("delete", template)}
    />
  );

  const tabsData = [
    profile.supportsJobTemplates && {
      id: "job",
      title: terms.jobTemplateTitle,
      content: renderOperationalPanel({
        type: TEMPLATE_TYPES.JOB,
        title: terms.jobTemplateTitle,
        subtitle: terms.jobTemplateSubtitle,
        emptyDescription: terms.jobTemplateEmpty,
        addLabel: `Add ${terms.job} Template`,
      }),
    },
    profile.supportsLegTemplates && {
      id: "leg",
      title: terms.legTemplateTitle,
      content: renderOperationalPanel({
        type: TEMPLATE_TYPES.LEG,
        title: terms.legTemplateTitle,
        subtitle: terms.legTemplateSubtitle,
        emptyDescription: terms.legTemplateEmpty,
        addLabel: `Add ${terms.leg} Template`,
      }),
    },
  ].filter(Boolean);

  return (
    <>
      <SettingsHeader title="Templates" />

      <div className="">
        <Tabs
          useSearchParams
          data={tabsData}
          default={0}
          fullHeight={false}
          tabContentClassName={(isActive) =>
            `inline-flex p-2 group focus:outline-none rounded-lg text-sm font-semibold focus-visible:outline-0 ${
              isActive
                ? "text-primary-600 bg-primary-50 dark:text-primary-500 dark:bg-primary-900/20 border-transparent"
                : "hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300 text-gray-700 border-transparent"
            }`
          }
          panelClasses="mt-2"
          tabListClasses="mb-1"
        />
      </div>

      <JobTemplateModal
        visible={modalState.visible && modalState.type === TEMPLATE_TYPES.JOB}
        template={modalState.template}
        mode={modalState.mode}
        onClose={handleCleanUp}
      />
      <LegTemplateModal
        visible={modalState.visible && modalState.type === TEMPLATE_TYPES.LEG}
        template={modalState.template}
        mode={modalState.mode}
        onClose={handleCleanUp}
      />
      <Modal
        title={confirmContent?.title}
        visible={showConfirmModal}
        setVisible={setShowConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        confirm={() => handleConfirm(confirmContent?.type)}
      >
        <div>{confirmContent?.content}</div>
      </Modal>
    </>
  );
};

export default Templates;
